/**
 * Stats Row Service
 *
 * Service layer for the E&T stats row, handling orchestration of the
 * StatsRowFormatter's variable map + per-tile/subtext rendering -- mirrors
 * EquityCardService.renderCardLines/getDefaultTemplate. `buildVariableMap`
 * is intentionally NOT a responsibility of this service; it lives on
 * StatsRowFormatter (see StatsRowFormatter.buildVariableMap), the same split
 * EquityCardService already establishes with EquityCardFormatter.
 *
 * Usage:
 *   import { StatsRowService } from './services/StatsRowService.js';
 *
 *   const row = StatsRowService.renderStatsRow(locals, clientData);
 *   // row.tiles[0..4] -> { state, label, value }
 *   // row.subtext -> rendered subtext string
 *   // row.vars -> flat variable map (for callers that need raw values too)
 */

import { StatsRowFormatter } from '../formatters/StatsRowFormatter.js';

const StatsRowService = {
	/**
	 * Render all 5 tiles + subtext for the E&T stats row.
	 *
	 * @param {Object} locals - The locals renderUserEquityModal already
	 *   computes today (totalContribution, roi, currentValue, UFL, narrow,
	 *   etc.) -- see StatsRowFormatter.buildVariableMap's jsdoc for the full
	 *   shape.
	 * @param {Object} [clientData] - Full client data object; clientData.language
	 *   supplies any per-client et_*_template overrides.
	 * @returns {Object}
	 *   {
	 *     tiles: Array<{state: string, label: (string|null), value: (string|null)}>, // index 0 = tile1 ... index 4 = tile5
	 *     subtext: string,
	 *     vars: Object // variable map, for callers needing raw values / conditional decisions
	 *   }
	 */
	renderStatsRow: function (locals, clientData) {
		const languageConfig = clientData?.language || {};
		const vars = StatsRowFormatter.buildVariableMap(locals || {});

		const tiles = [1, 2, 3, 4, 5].map((n) => StatsRowFormatter.renderTile(n, languageConfig, vars));
		const subtext = StatsRowFormatter.renderSubtext(languageConfig, vars);

		return { tiles, subtext, vars };
	},

	/**
	 * Get the default template for a given key (useful for displaying
	 * defaults in an admin UI, matching EquityCardService.getDefaultTemplate).
	 *
	 * @param {string} templateKey - e.g. 'et_tile1_label_template', 'et_subtext_template'
	 * @returns {string} - Default template string
	 */
	getDefaultTemplate: function (templateKey) {
		return StatsRowFormatter.getDefaultTemplate(templateKey);
	},
};

export { StatsRowService };
